import React, {
  useState,
  useCallback,
  useMemo,
}                                  from 'react';
import { History }                 from 'history';
import { FormikConfig }            from 'formik';
import { Breakpoint }              from '@material-ui/core/styles/createBreakpoints';
import {
  ILogo,
  ICreditUnion,
  IAccountFormValues,
  INote,
  ISalesPerson,
  ISalesFee,
  ISalesPersonFees,
  INoteValues,
  IFeeValues,
}                                  from '@typings/accountManagement';
import { ISelectOption }           from '@typings/components';
import { EAppPage }                from '@enums/common';
import { EAccountPage }            from '@enums/accountManagement';
import {
  EFeeUserType,
  EFeeType,
}                                  from '@enums/invoiceManagement';
import { useToggle }               from '@utils/useToggle';
import { useFetchPricing }         from '@modules/invoiceManagement/queries/invoiceManagementQueries';
import {
  useFetchAllSalesPersons,
  useCreateNewNote,
  useFetchNotes,
  useSaveAccount,
  useFetchNextCuid,
}                                  from '@modules/accountManagement/queries/accountQueries';
import { formatFullName }          from '@utils/commonFunctions';
import { stateOptions }            from '@utils/suggestions';
import {
  accountStatusOptions,
  SALESPERSON_DATA_BY_TYPE,
}                                  from '@modules/accountManagement/utils/suggestions';
import { Loader }                  from '@components/Loader/Loader';
import { AccountInfoPage }         from '../pages/AccountInfoPage';
import { BlankFieldsWarningModal } from '../components/BlankFieldsWarningModal';

const defaultFeeData: ISalesFee = {
  percentage    : null,
  salesPersonId : '',
  feeUserType   : EFeeUserType.Other,
  type          : EFeeType.Unknown,
};

const defaultFeesData: ISalesFee[] = [defaultFeeData];

const defaultSalesPersonsFees: ISalesPersonFees = {
  monthlyFees               : defaultFeesData,
  personalAdministrationFee : defaultFeesData,
  dealerAdministrationFee   : defaultFeesData,
  expressRecoveryFees       : defaultFeesData,
  deficiencyTrackingFees    : defaultFeesData,
  vrmFees                   : defaultFeesData,
  srsFees                   : defaultFeesData,
  reindeerFees              : defaultFeesData,
};

interface IProps extends Pick<History, 'push'> {
  width          : Breakpoint;
  isNewAccount   : boolean;
  currentAccount : ICreditUnion | null;
  currentLogo    : ILogo | null;
  cuid           : string;
}

export const AccountInfoContainer: React.FC<IProps> = ({
  push,
  width,
  isNewAccount,
  currentAccount,
  currentLogo,
  cuid,
}) => {
  const [accountData, setAccountData] = useState<Omit<IAccountFormValues, 'phoneNumber' | 'phoneExtension' | 'contactName'> | null>(null);
  const [noteToView, setNoteToView]   = useState<INote | null>(null);
  const [notes, setNotes]             = useState<INote[]>([]);

  const [isBlankFieldsModalOpen, toggleIsBlankFieldsModalOpen] = useToggle();
  const [isOpenNoteModal, toggleIsOpenNoteModal]               = useToggle();

  const handleToggleNoteModal = useCallback(
    (note: INote | null): void => {
      toggleIsOpenNoteModal();
      setNoteToView(note);
    },
    [toggleIsOpenNoteModal, setNoteToView],
  );

  // #region INTEGRATE API
  const { isLoading: isLoadingNotes } = useFetchNotes(cuid, {
    enabled   : !!cuid,
    onSuccess : (data) => setNotes(data),
  });

  const { mutateAsync: saveAccount, isLoading: isAccountSaving } = useSaveAccount();

  const {
    data       : salesPersonsResponse = [] as ISalesPerson[],
    isLoading  : isLoadingSalespersons,
    isFetching : isFetchingSalespersons,
  } = useFetchAllSalesPersons();

  const { mutateAsync: createNewNote } = useCreateNewNote({
    onSuccess: (data) => setNotes(data.reverse()),
  });

  const { data: defaultPricing = [] as IFeeValues[] } = useFetchPricing();

  const { data: nextCuid } = useFetchNextCuid({ enabled: isNewAccount });
  // #endregion

  const salesPersonsFees = useMemo<ISalesPersonFees>(
    () => Object.keys(defaultSalesPersonsFees).reduce(
      (acc, key: keyof ISalesPersonFees) => {
        const cuFee = currentAccount?.salesPersonFees?.[key];

        return {
          ...acc,
          [key]: cuFee?.length ? cuFee : defaultFeesData,
        };
      },
      {} as ISalesPersonFees,
    ),
    [currentAccount],
  );

  const salespersonList = useMemo<ISelectOption[]>(
    () => salesPersonsResponse.map((person) => ({
      label : (person.firstName && person.lastName) ? formatFullName(person) : person.organization,
      value : person.id,
    })),
    [salesPersonsResponse],
  );

  const addSalesPersonRow = useCallback(
    (
      blockName     : keyof ISalesPersonFees,
      data          : ISalesFee[],
      setFieldValue : (field: string, value: ISalesFee[], shouldValidate?: boolean) => void,
    ): void => {
      const { pricingKey, ...rowData } = SALESPERSON_DATA_BY_TYPE[blockName];

      const newRow: ISalesFee = {
        ...defaultFeeData,
        ...rowData,
      };

      setFieldValue(`salesPersonFees.${blockName}`,  [...data, newRow]);
    },
    [],
  );

  const removeSalesPersonRow = useCallback(
    (
      blockName     : keyof ISalesPersonFees,
      index         : number,
      data          : ISalesPersonFees,
      setFieldValue : (field: string, value: ISalesFee[], shouldValidate?: boolean) => void,
    ) => {
      const updatedData: ISalesFee[] = data[blockName];
      updatedData.splice(index, 1);

      setFieldValue(`salesPersonFees.${blockName}`, updatedData);
    },
    [],
  );

  const handleOnSubmitConfirmed = useCallback(
    async (accountValues: IAccountFormValues): Promise<void> => {
      if (!accountValues) {
        return;
      }

      await saveAccount({
        ...accountValues,
        id: currentAccount?.id,
      });

      push(`${EAppPage.AccountManagement}/${EAccountPage.Search}`);

      setAccountData(null);
      toggleIsBlankFieldsModalOpen();
    },
    [saveAccount, currentAccount, push, toggleIsBlankFieldsModalOpen],
  );

  const handleSubmitAccountForm = useCallback<FormikConfig<IAccountFormValues>['onSubmit']>(
    async ({
      salesPersonFees: salesPersonFeesValues,
      ...updatedAccount
    }) => {
      const salesPersonFeesKeys: (keyof ISalesPersonFees)[] = Object.keys(
        salesPersonFeesValues,
      ) as (keyof ISalesPersonFees)[];

      const salesPersonFees: ISalesPersonFees = salesPersonFeesKeys.reduce((acc, key) => ({
        ...acc,
        [key]: salesPersonFeesValues[key].filter((fee: ISalesFee) => !!fee.salesPersonId),
      }), {} as ISalesPersonFees);

      const {
        defaultLocationId,
        contactName,
        phoneExtension,
        phoneNumber,
        email,
        ...values
      } = updatedAccount;

      const requestData = {
        ...currentAccount,
        ...values,
        ...(defaultLocationId ? { defaultLocationId } : {}),
        defaultContactPerson: {
          ...currentAccount?.defaultContactPerson,
          email,
          contactName,
          phoneExtension,
          phoneNumber,
        },
        invoiceContactPerson: {
          ...currentAccount?.invoiceContactPerson,
          email: values.useDefaultLocationForInvoice ? email : values.invoiceLocation.email,
        },
        invoiceLocation: {
          ...currentAccount?.invoiceLocation,
          ...values.invoiceLocation,
        },
        salesPersonFees,
      };

      setAccountData(requestData as IAccountFormValues);

      const { name, city, state } = updatedAccount;
      const showConfirmation = !name || !state || !city;

      if (showConfirmation) {
        toggleIsBlankFieldsModalOpen();
      } else {
        await handleOnSubmitConfirmed(requestData as IAccountFormValues);
      }
    },
    [currentAccount, handleOnSubmitConfirmed, toggleIsBlankFieldsModalOpen],
  );

  const onAddNote = useCallback<FormikConfig<INoteValues>['onSubmit']>(
    async (
      { text },
      { resetForm },
    ) => {
      await createNewNote({ cuid, text });
      resetForm();
    },
    [createNewNote, cuid],
  );

  if (isLoadingSalespersons || isFetchingSalespersons || isLoadingNotes) {
    return (<Loader />);
  }

  return (
    <>
      {(isNewAccount || (!isNewAccount && currentAccount))
      && (!isLoadingSalespersons && !isFetchingSalespersons) && (
        <AccountInfoPage
          push                    = {push}
          accountData             = {currentAccount}
          accountLogo             = {currentLogo}
          defaultPricing          = {defaultPricing}
          nextCuid                = {nextCuid}
          states                  = {stateOptions}
          clientStatuses          = {accountStatusOptions}
          handleSubmitAccountForm = {handleSubmitAccountForm}
          salespersonList         = {salespersonList}
          salesPersonsFeeList     = {salesPersonsFees}
          isLoadingSalespersons   = {isLoadingSalespersons}
          addSalesPersonRow       = {addSalesPersonRow}
          removeSalesPersonRow    = {removeSalesPersonRow}
          notes                   = {notes}
          onAddNewNote            = {onAddNote}
          handleToggleNoteModal   = {handleToggleNoteModal}
          isOpenNoteModal         = {isOpenNoteModal}
          noteToView              = {noteToView}
        />
      )}

      {accountData && (
        <BlankFieldsWarningModal
          width           = {width}
          isOpen          = {isBlankFieldsModalOpen}
          isAccountSaving = {isAccountSaving}
          onCancel        = {toggleIsBlankFieldsModalOpen}
          onConfirm       = {() => handleOnSubmitConfirmed(accountData as IAccountFormValues)}
        />
      )}
    </>
  );
};
